<script setup lang="ts">
/**
 * BulkReceiveFailuresDialog
 *
 * Shown after a bulk milk receive operation when one or more containers
 * could not be created. Displays each failed container ID alongside the
 * reason returned by the API.
 */
import { DataTable, DialogModal } from '@tms-phoenix/ui-vue';
import Button from 'primevue/button';
import Column from 'primevue/column';
import type { BulkReceiveFailure } from '../types';

defineOptions({ inheritAttrs: false });

defineProps<{
  visible: boolean;
  failures: BulkReceiveFailure[];
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="$t('inventory.bulkReceiveFailuresTitle')"
    max-width="max-w-xl"
    position="center"
    @update:visible="emit('update:visible', $event)"
  >
    <p class="text-sm text-gray-600 mb-4">
      {{ $t('inventory.bulkReceiveFailuresSubtitle') }}
    </p>
    <div class="border border-gray-200 rounded-lg overflow-hidden">
      <DataTable
        :value="failures"
        class="w-full text-sm"
        data-key="containerId"
      >
        <Column
          field="containerId"
          :header="$t('inventory.containerId')"
          style="width: 40%"
        >
          <template #body="{ data }">
            <span class="font-mono text-gray-900">{{ data.containerId }}</span>
          </template>
        </Column>
        <Column
          field="reason"
          :header="$t('common.reason')"
        >
          <template #body="{ data }">
            <span class="text-red-600">{{ data.reason }}</span>
          </template>
        </Column>
      </DataTable>
    </div>
    <template #footer>
      <div class="w-full flex justify-end">
        <Button
          :label="$t('common.close')"
          severity="secondary"
          @click="emit('update:visible', false)"
        />
      </div>
    </template>
  </DialogModal>
</template>
